/**
 * User settings + vehicle profiles. localStorage is the instant/offline cache;
 * authenticated users are synchronized to the server by userSettingsSync.ts.
 * Spotify pins remain separate because they also belong to a Spotify account.
 *
 * Vehicle dimensions are passed straight through to the Mapbox Directions API
 * (`max_height`, `max_width`, `max_weight`), which routes the vehicle away from
 * roads carrying a lower posted limit. That is what keeps the Hino off low
 * bridges and weight-restricted sois.
 */

export type VehicleKind = 'car' | 'truck';

export interface VehicleProfile {
  id: string;
  name: string;
  kind: VehicleKind;
  /** metres */
  height?: number;
  /** metres */
  width?: number;
  /** metric tonnes */
  weight?: number;
  /** litres per 100 km, used only for the trip log */
  consumption?: number;
  /**
   * This vehicle carries an Easy Pass / M-Pass transponder.
   *
   * Per vehicle rather than per account, because the truck and the car do not
   * necessarily both have one - and it changes routing: with a transponder the
   * cost of a toll is the fee, not ten minutes in the cash queue, so toll roads
   * stay in play and only cash-only plazas are excluded.
   */
  easyPass?: boolean;
}

export type UnitSystem = 'metric' | 'imperial';

/**
 * How much noise the app makes.
 *
 * 'alerts' exists because a full spoken instruction is often more than the
 * driver wants on a road they know - but they still want to be told when
 * something is coming. A loud chime carries that without narration.
 */
export type SoundMode = 'voice' | 'alerts' | 'muted';
export type MapTheme = 'night' | 'day' | 'auto';
export type AppLanguage = 'en' | 'th';

/**
 * Which app fills the media bar.
 *
 * Spotify has a real Web API, so its bar shows the track and controls
 * playback. Audible offers no public API at all - nothing outside Amazon's
 * own apps may see or control playback - so its bar is a launcher: it opens
 * the Audible app, and play/pause stays with the app or the car's controls.
 */
export type AudioSource = 'spotify' | 'audible';
export type TrafficMode = 'off' | 'bangkok' | 'all';

export interface RoutePrefs {
  avoidTolls: boolean;
  avoidMotorways: boolean;
  avoidFerries: boolean;
  avoidUnpaved: boolean;
  /** Ask Mapbox for up to 3 routes and let the driver choose. */
  alternatives: boolean;
  /** Where Mapbox's live-traffic profile is allowed to influence routing. */
  trafficMode: TrafficMode;
  /** @deprecated migrated to `trafficMode`; retained only for old stored settings. */
  useTraffic?: boolean;
}

import { DEFAULT_SIGNAL_SECONDS, DEFAULT_SPEEDS, type PaceSpeeds } from '../nav/paceModel';

export interface Settings {
  units: UnitSystem;
  theme: MapTheme;
  /** UI + turn-instruction language. Mapbox generates Thai instructions natively. */
  language: AppLanguage;
  /** @deprecated superseded by `soundMode`; kept so old installs migrate. */
  voiceEnabled: boolean;
  soundMode: SoundMode;
  /**
   * Volume of speech and chimes, 0..1.5 - independent of the media volume.
   *
   * Above 1.0 is a real boost, but only for the chime: the Web Speech API caps
   * `utterance.volume` at 1.0 and there is no way around it, because the
   * synthesiser's output is never exposed to WebAudio where a gain stage could
   * be applied. What the extra range does instead is duck the music much harder
   * and drive the chime through a gain stage, which is what actually makes the
   * instruction audible over a diesel at 100 km/h.
   */
  voiceVolume: number;
  /** Pause/lower Spotify while an instruction is spoken. */
  duckMusic: boolean;
  /** Which app the media bar is for; see AudioSource. */
  audioSource: AudioSource;
  /**
   * Shows and playlists pinned to the media bar's quick row, in the order the
   * driver arranged them.
   *
   * Stored as {uri,name,image,kind} rather than a bare uri list so the row can
   * paint immediately at boot - resolving names and art would mean a Spotify
   * round trip before anything appears, and the row would flash empty on every
   * cold start in the car. Refreshed from the API when the picker is opened.
   */
  spotifyPins: SpotifyPin[];
  /**
   * Watch for congestion ahead while navigating and offer an easier route
   * when one saves real time. Offer only - it never reroutes on its own.
   */
  trafficWatch: boolean;
  /** Keep the screen awake while navigating. */
  keepAwake: boolean;
  /** Auto zoom/pitch into junctions as they approach. */
  adaptiveCamera: boolean;
  /**
   * Camera tilt in degrees, used both driving and browsing - one look, two
   * modes. Snapped to five levels (0/15/30/45/60) because a continuous dial
   * over an abstract quantity just invites fiddling at the wheel. 60 is the
   * classic behind-the-car view the app always had; 0 is a flat paper map.
   */
  cameraPitch: 0 | 15 | 30 | 45 | 60;
  /**
   * Fixed cruise zoom while navigating; 0 means auto (scale out with speed).
   * A fixed level still zooms IN for junction approaches - that part is
   * guidance, not preference - it only stops the speed-based drift.
   */
  driveZoom: number;
  activeVehicleId: string;
  vehicles: VehicleProfile[];
  route: RoutePrefs;
  /** Top-speed ceiling per road tier, km/h. See nav/paceModel.ts. */
  speeds: PaceSpeeds;
  /** Seconds lost at an average red light. Thai signal cycles are long. */
  signalSeconds: number;
  /**
   * How bad a surface has to be before the app calls it rough, 1 (only truly
   * broken roads) to 5 (mention anything). Isaan back roads are rough as a
   * baseline, so a fixed threshold either cries wolf constantly or says
   * nothing; the driver sets where normal ends.
   */
  roughSensitivity: 1 | 2 | 3 | 4 | 5;
  /** Let known-rough roads push the routing away from them, not just warn. */
  avoidRough: boolean;
  /** 24-hour clock for ETAs. Default on: it is what the dashboard shows. */
  clock24: boolean;
  /**
   * Ask the advisor for a second opinion when several routes are offered.
   *
   * Off by default. The routing, the signal counts and the road-width figures
   * already answer the question most of the time, and a model that costs money
   * and a few seconds to agree with them is not worth running on every search.
   * It earns its keep on the unfamiliar journey, so it stays available.
   */
  aiAdvice: boolean;
  /**
   * Overlay of roads we have driven: off, coloured by the speed achieved, or
   * coloured by how rough the surface was.
   */
  drivenOverlay: 'off' | 'speed' | 'rough';
  /** Per-user dotted trail of the last 500 km driven; see nav/breadcrumbs.ts. */
  breadcrumbTrail: boolean;
  /**
   * Extruded 3D buildings from z15. Off by default: they are context, not
   * guidance, and drawing a city costs GPU a head unit may not have spare.
   */
  buildings3d: boolean;
  /** Cache map tiles for offline use. */
  offlineTiles: boolean;
  logTrips: boolean;
  /**
   * Size of the maneuver sign (the green "arrows popup"). On low-resolution
   * phones the default sign eats a third of the screen, so the driver can
   * pick Compact/Normal/Large by hand; auto compacts on narrow screens and
   * otherwise keeps today's behavior. /car has its own banner and ignores
   * this.
   */
  signSize: 'auto' | 'compact' | 'normal' | 'large';
  /**
   * Fine multiplier on top of the size preset, percent (40-125). The presets
   * step in coarse jumps; on genuinely small screens even Compact can still
   * feel big, and this dials the whole sign down smoothly. Applied only to
   * the floating sign - the landscape panel and /car banner own their
   * geometry.
   */
  signScale: number;
  /**
   * Relative height of the top maneuver bar, percent (60-125).
   *
   * Kept separate from `signScale` so a head unit can use a taller or tighter
   * bar without distorting its type. The renderer treats this as a safe
   * minimum and lets real content grow, so a small value never clips guidance.
   */
  signBarHeight: number;
  /**
   * Size of the detached lane-arrow guide on the car screen, percent (90-150).
   * Phone lane arrows remain part of the turn sign and follow `signScale`.
   */
  laneGuideScale: number;
  /**
   * Height of the route-picker sheet, as a percentage of the viewport.
   *
   * Dragged by its grab handle and remembered, because the right size depends
   * on the screen it is on. **0 means auto**, and auto is not one number: half
   * the display is generous on a 1280-tall head unit and starves the route list
   * on a 480-tall one, where the same percentage leaves the options with no
   * room at all once the destination and buttons have taken theirs. Below ~42
   * the sheet also switches to compact type so the rows still fit.
   */
  previewHeight: number;
}

export const DEFAULT_VEHICLES: VehicleProfile[] = [
  {
    id: 'car',
    name: 'Car',
    kind: 'car',
    height: 1.95,
    width: 1.85,
    weight: 2.3,
    consumption: 10.5,
  },
  {
    id: 'hino',
    name: 'Hino Truck',
    kind: 'truck',
    // Conservative defaults for a Hino 300/500 series box body - edit in Settings.
    height: 3.6,
    width: 2.3,
    weight: 8.5,
    consumption: 22,
  },
];

/**
 * A pinned show or playlist as persisted.
 *
 * Deliberately a structural copy of PinnedItem rather than an import from
 * audio/spotify: settings is core and must not depend on the audio layer, and
 * this shape is a storage record with its own compatibility concerns.
 */
export interface SpotifyPin {
  uri: string;
  name: string;
  image: string | null;
  /**
   * 'liked' is Liked Songs, which is NOT a playlist however much it looks like
   * one: it has no context URI that can be played reliably, so it is started by
   * reading the saved tracks and passing their URIs. Its `uri` is the sentinel
   * 'wayfarer:liked' - never sent to Spotify, only matched on.
   */
  kind: 'show' | 'playlist' | 'liked';
}

/** Sentinel URI for the Liked Songs pin; see SpotifyPin.kind. */
export const LIKED_URI = 'wayfarer:liked';

export const DEFAULT_SETTINGS: Settings = {
  units: 'metric',
  theme: 'auto',
  language: 'en',
  voiceEnabled: true,
  soundMode: 'voice',
  voiceVolume: 1,
  duckMusic: true,
  audioSource: 'spotify',
  spotifyPins: [],
  trafficWatch: true,
  keepAwake: true,
  adaptiveCamera: true,
  cameraPitch: 60,
  driveZoom: 0,
  activeVehicleId: 'car',
  vehicles: DEFAULT_VEHICLES,
  route: {
    avoidTolls: false,
    avoidMotorways: false,
    avoidFerries: true,
    avoidUnpaved: false,
    alternatives: true,
    trafficMode: 'bangkok',
  },
  speeds: DEFAULT_SPEEDS,
  signalSeconds: DEFAULT_SIGNAL_SECONDS,
  // Deliberately lenient by default: rough IS normal here, and an app that
  // says so every two kilometres gets ignored.
  roughSensitivity: 2,
  avoidRough: true,
  clock24: true,
  aiAdvice: false,
  drivenOverlay: 'rough',
  breadcrumbTrail: true,
  buildings3d: false,
  offlineTiles: true,
  logTrips: true,
  signSize: 'auto',
  signScale: 100,
  signBarHeight: 100,
  laneGuideScale: 100,
  previewHeight: 0,
};

/** The sheet may be dragged between these, as a percentage of viewport height. */
export const PREVIEW_HEIGHT_MIN = 26;
export const PREVIEW_HEIGHT_MAX = 88;
/** Below this the route rows switch to compact type. */
export const PREVIEW_HEIGHT_COMPACT = 42;

/**
 * The auto height for a viewport, used until the driver drags the handle.
 *
 * A short screen needs a BIGGER share, not the same one: the destination
 * header, origin row and buttons cost a fixed number of pixels whatever the
 * display, so on 480px they eat a half-height sheet whole and the route options
 * - the entire reason the screen exists - end up with nothing.
 */
export function autoPreviewHeight(viewportHeight: number): number {
  if (viewportHeight <= 500) return 78;
  if (viewportHeight <= 620) return 70;
  if (viewportHeight <= 800) return 60;
  return 52;
}

const KEY = 'wayfarer.settings.v1';

/**
 * The stock car profile used to be branded "Alphard". It is now the generic
 * "Car" - drivers add their own vehicles instead - so existing installs get
 * renamed in place rather than silently keeping a name nobody chose. A profile
 * the driver has already renamed themselves is left alone.
 */
function migrateVehicles(vehicles: VehicleProfile[]): VehicleProfile[] {
  return vehicles.map((v) =>
    v.id === 'alphard' ? { ...v, id: 'car', name: v.name === 'Alphard' ? 'Car' : v.name } : v,
  );
}

export function mergeSettings(base: Settings, patch: unknown): Settings {
  if (!patch || typeof patch !== 'object') return base;
  const p = patch as Partial<Settings>;
  const vehicles =
    Array.isArray(p.vehicles) && p.vehicles.length ? migrateVehicles(p.vehicles) : base.vehicles;
  const activeVehicleId = p.activeVehicleId === 'alphard' ? 'car' : p.activeVehicleId;
  // Installs from before sound modes existed carry only `voiceEnabled`; keep
  // their choice rather than silently switching them back on.
  const soundMode: SoundMode =
    p.soundMode ?? (p.voiceEnabled === false ? 'muted' : base.soundMode);
  const savedRoute = (p.route ?? {}) as Partial<RoutePrefs>;
  const trafficMode: TrafficMode =
    savedRoute.trafficMode === 'off' ||
    savedRoute.trafficMode === 'bangkok' ||
    savedRoute.trafficMode === 'all'
      ? savedRoute.trafficMode
      : typeof savedRoute.useTraffic === 'boolean'
        ? savedRoute.useTraffic
          ? 'bangkok'
          : 'off'
        : base.route.trafficMode;

  /*
   * Pins come back from storage (and from a synced settings blob) as unknown
   * shape - the `...p` spread below would otherwise hand the media bar objects
   * with no uri and it would render blank buttons that do nothing.
   */
  const spotifyPins = Array.isArray(p.spotifyPins)
    ? (p.spotifyPins as unknown[]).filter(
        (x): x is SpotifyPin =>
          !!x &&
          typeof x === 'object' &&
          typeof (x as SpotifyPin).uri === 'string' &&
          typeof (x as SpotifyPin).name === 'string',
      )
    : base.spotifyPins;

  return {
    ...base,
    ...p,
    spotifyPins,
    soundMode,
    roughSensitivity: (p.roughSensitivity ?? base.roughSensitivity) as Settings['roughSensitivity'],
    activeVehicleId: activeVehicleId ?? base.activeVehicleId,
    route: { ...base.route, ...savedRoute, trafficMode },
    // Deep-merge so a settings blob saved before these existed still picks up
    // sane defaults instead of an undefined speed and a NaN ETA.
    speeds: { ...base.speeds, ...(p.speeds ?? {}) },
    signalSeconds:
      typeof p.signalSeconds === 'number' && p.signalSeconds >= 0
        ? p.signalSeconds
        : base.signalSeconds,
    // Clamped: a corrupt store must never render an invisible or giant sign.
    signScale:
      typeof p.signScale === 'number' && p.signScale >= 40 && p.signScale <= 125
        ? Math.round(p.signScale)
        : base.signScale,
    signBarHeight:
      typeof p.signBarHeight === 'number' && p.signBarHeight >= 60 && p.signBarHeight <= 125
        ? Math.round(p.signBarHeight)
        : base.signBarHeight,
    laneGuideScale:
      typeof p.laneGuideScale === 'number' && p.laneGuideScale >= 90 && p.laneGuideScale <= 150
        ? Math.round(p.laneGuideScale)
        : base.laneGuideScale,
    // Never let a stored empty list orphan the vehicle picker.
    vehicles,
  };
}

/** Create a blank profile the driver can fill in. */
export function newVehicle(kind: VehicleKind = 'car'): VehicleProfile {
  const base: VehicleProfile =
    kind === 'truck'
      ? { id: '', name: 'New truck', kind, height: 3.6, width: 2.3, weight: 8.5, consumption: 22 }
      : { id: '', name: 'New vehicle', kind, height: 1.8, width: 1.8, weight: 1.8, consumption: 8 };
  return {
    ...base,
    id: `v${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`,
  };
}

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return mergeSettings(DEFAULT_SETTINGS, JSON.parse(raw));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

/** Settings owned by the Wayfarer user record rather than a Spotify account. */
export type UserSettings = Omit<Settings, 'spotifyPins'>;

/**
 * Produce the complete, stable settings record sent to the server.
 *
 * Explicit deep copies keep later vehicle/route edits from mutating an
 * in-flight request or a cached revision. Spotify pins have their own per-user
 * sync and must never be duplicated into this record.
 */
export function userSettings(s: Settings): UserSettings {
  return {
    units: s.units,
    theme: s.theme,
    language: s.language,
    voiceEnabled: s.voiceEnabled,
    soundMode: s.soundMode,
    voiceVolume: s.voiceVolume,
    duckMusic: s.duckMusic,
    audioSource: s.audioSource,
    trafficWatch: s.trafficWatch,
    keepAwake: s.keepAwake,
    adaptiveCamera: s.adaptiveCamera,
    cameraPitch: s.cameraPitch,
    driveZoom: s.driveZoom,
    activeVehicleId: s.activeVehicleId,
    vehicles: s.vehicles.map((vehicle) => ({
      id: vehicle.id,
      name: vehicle.name,
      kind: vehicle.kind,
      ...(typeof vehicle.height === 'number' ? { height: vehicle.height } : {}),
      ...(typeof vehicle.width === 'number' ? { width: vehicle.width } : {}),
      ...(typeof vehicle.weight === 'number' ? { weight: vehicle.weight } : {}),
      ...(typeof vehicle.consumption === 'number' ? { consumption: vehicle.consumption } : {}),
      easyPass: vehicle.easyPass === true,
    })),
    route: {
      avoidTolls: s.route.avoidTolls,
      avoidMotorways: s.route.avoidMotorways,
      avoidFerries: s.route.avoidFerries,
      avoidUnpaved: s.route.avoidUnpaved,
      alternatives: s.route.alternatives,
      trafficMode: s.route.trafficMode,
    },
    speeds: {
      motorway: s.speeds.motorway,
      dual: s.speeds.dual,
      single: s.speeds.single,
    },
    signalSeconds: s.signalSeconds,
    roughSensitivity: s.roughSensitivity,
    avoidRough: s.avoidRough,
    clock24: s.clock24,
    aiAdvice: s.aiAdvice,
    drivenOverlay: s.drivenOverlay,
    breadcrumbTrail: s.breadcrumbTrail,
    buildings3d: s.buildings3d,
    offlineTiles: s.offlineTiles,
    logTrips: s.logTrips,
    signSize: s.signSize,
    signScale: s.signScale,
    signBarHeight: s.signBarHeight,
    laneGuideScale: s.laneGuideScale,
    previewHeight: s.previewHeight,
  };
}

export function saveSettings(s: Settings): void {
  try {
    // Pins used to live in this device-global blob. Leaving them here makes a
    // second driver inherit the first driver's Spotify shortcuts before auth
    // can identify whose cache belongs on screen.
    localStorage.setItem(KEY, JSON.stringify({ ...s, spotifyPins: [] }));
  } catch {
    /* private mode / quota - settings simply won't persist */
  }
}

export function activeVehicle(s: Settings): VehicleProfile {
  return s.vehicles.find((v) => v.id === s.activeVehicleId) ?? s.vehicles[0];
}
